import type { IpcListener, RouterActionPayload } from './browser-types'
import { applyStoreSnapshotMutation, getStoreSnapshot, MAIN_PROCESS_ORIGIN_ID } from './browser-store'
import { refreshBrowserBillingState } from './browser-billing'
import { resolveRouterAction } from './browser-router'
import type { IBroadcastApi } from '@highlight/ipc'
import { getInvokeChannel } from '@highlight/ipc/invoke-channel'

class BrowserTransport {
  private readonly listeners = new Map<string, Set<IpcListener>>()
  private readonly channel: string

  constructor(channel: string) {
    this.channel = channel
  }

  private emit(event: string, ...args: Array<unknown>): void {
    const callbacks = this.listeners.get(event)
    if (!callbacks || callbacks.size === 0) {
      return
    }

    for (const callback of Array.from(callbacks)) {
      callback(...args)
    }
  }

  on = (event: string, callback: IpcListener): void => {
    const callbacks = this.listeners.get(event) ?? new Set<IpcListener>()
    callbacks.add(callback)
    this.listeners.set(event, callbacks)
  }

  once = (event: string, callback: IpcListener): void => {
    const wrappedCallback: IpcListener = (...args: Array<unknown>) => {
      this.off(event, wrappedCallback)
      callback(...args)
    }
    this.on(event, wrappedCallback)
  }

  off = (event: string, callback: IpcListener): void => {
    const callbacks = this.listeners.get(event)
    if (!callbacks) {
      return
    }

    callbacks.delete(callback)
    if (callbacks.size === 0) {
      this.listeners.delete(event)
    }
  }

  private handleEventsChannelSend = (namespace: string, args: Array<unknown>): void => {
    const originId = args[0]
    const eventName = typeof args[1] === 'string' ? args[1] : undefined
    if (!eventName) {
      return
    }

    const eventArgs = args.slice(2)
    applyStoreSnapshotMutation(namespace, eventName, eventArgs)

    if (originId === MAIN_PROCESS_ORIGIN_ID && !eventName.startsWith('renderer:')) {
      this.emit(namespace, MAIN_PROCESS_ORIGIN_ID, eventName, ...eventArgs)
    }

    if (eventName === 'renderer:verifyState') {
      const processId = typeof eventArgs[0] === 'string' ? eventArgs[0] : undefined
      if (!processId) {
        return
      }
      this.emit(namespace, MAIN_PROCESS_ORIGIN_ID, `main:verifyState:${processId}`, true)
      return
    }

    if (eventName === 'renderer:syncWithMain') {
      const processId = typeof eventArgs[0] === 'string' ? eventArgs[0] : undefined
      if (!processId) {
        return
      }
      this.emit(namespace, MAIN_PROCESS_ORIGIN_ID, `main:syncWithRenderer:${processId}`, getStoreSnapshot(namespace))
      return
    }

    if (eventName === 'renderer:batchSyncWithMain') {
      const processId = typeof eventArgs[0] === 'string' ? eventArgs[0] : undefined
      const requestedNamespaces = Array.isArray(eventArgs[1]) ? eventArgs[1] : []
      if (!processId) {
        return
      }

      const states: Record<string, Record<string, unknown>> = {}
      for (const requestedNamespace of requestedNamespaces) {
        if (typeof requestedNamespace !== 'string') {
          continue
        }
        states[requestedNamespace] = getStoreSnapshot(requestedNamespace)
      }
      this.emit(namespace, MAIN_PROCESS_ORIGIN_ID, `main:batchSyncWithRenderer:${processId}`, states)
      return
    }

    if (namespace === 'global.events' && eventName === 'billing:refresh') {
      void refreshBrowserBillingState(true)
    }
  }

  send = (event: string, ...args: Array<unknown>): void => {
    if (this.channel === 'events') {
      this.handleEventsChannelSend(event, args)
      return
    }

    if (this.channel !== 'router') {
      return
    }

    const [namespace, mode] = event.split(':')
    if (mode !== 'async') {
      return
    }

    const routerAction = args[1] as RouterActionPayload | undefined
    if (!routerAction?.action || !routerAction?.actionId) {
      return
    }

    Promise.resolve(resolveRouterAction(namespace, routerAction.action, routerAction.payload))
      .then((response) => {
        this.emit(`${namespace}:async:${routerAction.actionId}`, response)
      })
      .catch((error: unknown) => {
        const ipcError =
          error instanceof Error
            ? {
                code: '',
                message: error.message,
                stack: error.stack ?? '',
                name: error.name,
              }
            : {
                code: '',
                message: 'Unknown browser IPC error',
                stack: '',
                name: 'Error',
              }

        this.emit(`${namespace}:async:${routerAction.actionId}`, {
          _ipcError: ipcError,
        })
      })
  }

  sendSync = (event: string, ...args: Array<unknown>): unknown => {
    if (this.channel !== 'router') {
      return undefined
    }

    const [namespace, mode] = event.split(':')
    if (mode !== 'sync') {
      return undefined
    }

    const routerAction = args[1] as RouterActionPayload | undefined
    if (!routerAction?.action) {
      return undefined
    }

    return resolveRouterAction(namespace, routerAction.action, routerAction.payload)
  }

  invoke = async (event: string): Promise<unknown> => {
    if (this.channel === 'events' && event === getInvokeChannel('global.events', 'billing:refresh-and-wait')) {
      return refreshBrowserBillingState(true)
    }
    if (event === 'exportDebugLogsZip') {
      return null
    }
    return undefined
  }
}

export const buildChannelsProxy = (): Record<string, BrowserTransport> => {
  const channelMap = new Map<string, BrowserTransport>()
  const getTransport = (channel: string): BrowserTransport => {
    const existing = channelMap.get(channel)
    if (existing) {
      return existing
    }
    const transport = new BrowserTransport(channel)
    channelMap.set(channel, transport)
    return transport
  }

  return new Proxy<Record<string, BrowserTransport>>(
    {},
    {
      get: (_target, property: string | symbol) => {
        if (typeof property !== 'string') {
          return undefined
        }
        return getTransport(property)
      },
    },
  )
}

const warnedMissingIpcMethods = new Set<string>()

const getMissingIpcMethodFallback = (methodName: string) => {
  return (..._args: Array<unknown>) => {
    if (!warnedMissingIpcMethods.has(methodName)) {
      warnedMissingIpcMethods.add(methodName)
      console.warn(`[browser-runtime] Missing ipc.${methodName} shim; using a no-op fallback.`)
    }
    return undefined
  }
}

export const buildBrowserBroadcastShim = (): IBroadcastApi => {
  const subscribers = new Map<string, Set<(data: unknown) => void>>()

  return {
    registerProducer: () => undefined,
    unregisterProducer: () => undefined,
    registerConsumer: () => undefined,
    unregisterConsumer: () => undefined,
    publish: (topic: string, data: unknown) => {
      for (const callback of subscribers.get(topic) ?? []) {
        try {
          callback(data)
        } catch (error) {
          console.warn(`[browser-runtime] Broadcast subscriber failed for topic "${topic}"`, error)
        }
      }
    },
    subscribe: (topic: string, callback: (data: unknown) => void) => {
      const topicSubscribers = subscribers.get(topic) ?? new Set<(data: unknown) => void>()
      topicSubscribers.add(callback)
      subscribers.set(topic, topicSubscribers)

      return () => {
        topicSubscribers.delete(callback)
        if (topicSubscribers.size === 0) {
          subscribers.delete(topic)
        }
      }
    },
  }
}

export const buildIpcShim = (): Window['ipc'] => {
  const baseIpc = {
    channels: buildChannelsProxy(),
    broadcast: buildBrowserBroadcastShim(),
    publishInteractiveFrame: () => undefined,
    setChatInputFocused: () => undefined,
    onDebugInteractivityChange: () => {
      return () => undefined
    },
    openDebugLogs: () => undefined,
    exportDebugLogsZip: async () => null,
    debugExtension: async () => ({}),
    isAlwaysOnTop: async () => false,
    setAlwaysOnTop: async () => false,
  }

  return new Proxy(baseIpc as Window['ipc'] & Record<string, unknown>, {
    get: (target, property: string | symbol) => {
      if (typeof property !== 'string') {
        return Reflect.get(target, property)
      }
      const existingValue = Reflect.get(target, property)
      if (typeof existingValue !== 'undefined') {
        return existingValue
      }
      return getMissingIpcMethodFallback(property)
    },
  }) as Window['ipc']
}
